import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Map as MapIcon,
  BarChart as ChartIcon,
  Security as CopIcon,
  Article as BriefingIcon,
  GridView as BlankIcon,
} from '@mui/icons-material';
import type { DashboardTemplate } from './dashboardTemplates';
import { getDashboardTemplates } from './dashboardTemplates';

const ICONS: Record<string, React.ReactNode> = {
  blank: <BlankIcon sx={{ fontSize: 32 }} />,
  overview: <MapIcon sx={{ fontSize: 32 }} />,
  analytics: <ChartIcon sx={{ fontSize: 32 }} />,
  cop: <CopIcon sx={{ fontSize: 32 }} />,
  briefing: <BriefingIcon sx={{ fontSize: 32 }} />,
};

interface MiniPreviewProps {
  cells: Array<[number, number, number, number]>;
  selected?: boolean;
}

const MiniPreview: React.FC<MiniPreviewProps> = ({ cells, selected }) => {
  const theme = useTheme();
  const maxY = cells.reduce((m, [, y, , h]) => Math.max(m, y + h), 0);
  const cols = 12;
  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: `${cols} / ${maxY}`,
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: selected ? 'action.selected' : 'action.hover',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'border-color 0.2s',
      }}
    >
      {cells.map(([x, y, w, h], idx) => (
        <Box
          key={idx}
          sx={{
            position: 'absolute',
            left: `${(x / cols) * 100}%`,
            top: `${(y / maxY) * 100}%`,
            width: `${(w / cols) * 100}%`,
            height: `${(h / maxY) * 100}%`,
            boxSizing: 'border-box',
            p: '2px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 0.5,
              bgcolor: selected ? theme.palette.primary.main : theme.palette.text.disabled,
              opacity: selected ? 0.7 : 0.35,
              transition: 'all 0.2s',
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

interface DashboardTemplatePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: DashboardTemplate) => void;
}

const DashboardTemplatePicker: React.FC<DashboardTemplatePickerProps> = ({ open, onClose, onSelect }) => {
  const templates = getDashboardTemplates();
  const [selectedId, setSelectedId] = useState<string>(templates[0]?.id ?? '');

  const selected = templates.find((t) => t.id === selectedId);

  const handleCreate = () => {
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose a template</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a starting layout for your dashboard. You can customize everything after creation.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 2,
          }}
        >
          {templates.map((tpl) => {
            const isSelected = tpl.id === selectedId;
            return (
              <Card
                key={tpl.id}
                variant="outlined"
                sx={{
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderWidth: isSelected ? 2 : 1,
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  boxShadow: isSelected ? 4 : 0,
                }}
              >
                <CardActionArea
                  onClick={() => setSelectedId(tpl.id)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Box sx={{ p: 1.5, pb: 0 }}>
                    <MiniPreview cells={tpl.previewCells} selected={isSelected} />
                  </Box>
                  <CardContent sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      {ICONS[tpl.id] ?? <BlankIcon sx={{ fontSize: 20 }} />}
                      <Typography variant="subtitle2" fontWeight={700}>
                        {tpl.name}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {tpl.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!selected}>
          Create with this template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardTemplatePicker;
